// Pure logic for the multi-select chip control rendered by CheckQuestion.
// Lives in its own file so vitest (node environment, no react-native runtime
// available) can import nextSelection without pulling in the .tsx file's
// react-native + react-native-paper imports.

// Options that mean "nothing applies" — mutually exclusive with all others.
// Must be exact matches to avoid false positives (e.g. "No Concentrated Sweets"
// is a real option). Same set the previous CheckQuestion enforced; preserved
// verbatim for the flat-section redesign (no functional change).
export const EXCLUSIVE_VALUES = new Set([
    "None",
    "None of the above",
    "None Reported/Observed",
    "No Restrictions",
    "No texture modifications",
    "N/A",
    "Denies all",
    "WNL",
    "Within Normal Limits",
    "Oriented x3",
    "Not oriented"
]);

/**
 * Pairwise exclusions: when the key option is tapped, any currently-selected
 * value in its list is auto-cleared. Symmetric — each pair listed in both
 * directions so the lookup is O(1) and the toggle stays branch-free. Use this
 * only for option combinations that are clinically impossible (not just
 * uncommon). For "this option means nothing else applies", use
 * EXCLUSIVE_VALUES above.
 *
 * Mobile-only enforcement: server stores whatever the API receives. The web
 * export and any non-mobile client can still produce these combinations;
 * option-level exclusivity at the schema/server tier is a separate, larger
 * change.
 *
 * Exact match on option text, same trade-off as EXCLUSIVE_VALUES — if the
 * option text changes in the DB this stops working until the strings here are
 * updated.
 */
export const PAIRWISE_EXCLUDES: Record<string, string[]> = {
    // Q15 — Hearing: partial loss XOR complete loss.
    "Hearing Loss (partial)": ["Deaf (complete loss)"],
    "Deaf (complete loss)": ["Hearing Loss (partial)"],
    // Q188 — Eye/vision: "Correctable" XOR "not correctable", and
    // "Correctable" XOR "Legally Blind" (Legally Blind is non-correctable by
    // definition). "not correctable" + "Legally Blind" is a valid
    // co-selection and is NOT excluded.
    "Visual Impairment (Correctable with glasses)": [
        "Visual Impairment (not correctable with glasses)",
        "Legally Blind"
    ],
    "Visual Impairment (not correctable with glasses)": [
        "Visual Impairment (Correctable with glasses)"
    ],
    "Legally Blind": ["Visual Impairment (Correctable with glasses)"]
};

/**
 * Pure: given the current selection and the value just tapped, return the
 * new selection.
 *
 * Branches (must compose):
 *   1. Tapping an already-selected value → toggle off (no other changes).
 *   2. Tapping an EXCLUSIVE_VALUES entry → replace everything with just it.
 *   3. Tapping a normal value → drop any EXCLUSIVE_VALUES present + drop any
 *      values listed in this option's PAIRWISE_EXCLUDES, then add it.
 */
export function nextSelection(selected: string[], optionValue: string): string[] {
    if (selected.includes(optionValue)) {
        return selected.filter((v) => v !== optionValue);
    }
    if (EXCLUSIVE_VALUES.has(optionValue)) {
        return [optionValue];
    }
    const pairwise = PAIRWISE_EXCLUDES[optionValue] ?? [];
    const drop = new Set(pairwise);
    const next = selected.filter((v) => !EXCLUSIVE_VALUES.has(v) && !drop.has(v));
    next.push(optionValue);
    return next;
}
